import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { OwnerContext } from '../types/actividad';
import {
  ArrowLeft,
  Briefcase,
  Package,
  PackageSearch,
  Boxes,
  ShoppingCart,
  Receipt,
  Edit2,
  Calendar,
  Plus,
  Trash2,
  AlertTriangle,
  Archive,
  Info,
  Tag,
  FolderOpen,
  MoreHorizontal,
  Layers,
  CheckCircle,
  XCircle,
  RefreshCw,
  Gift,
  Clock
} from 'lucide-react';
import { useLumisial } from '../context/LumisialContext';
import { VolverButton } from '../components/ui/VolverButton';
import { actividadService } from '../services/actividadService';
import { produccionService, ProduccionConMetricas } from '../services/produccionService';
import { productoActividadService } from '../services/productoActividadService';
import { ventaActividadService } from '../services/ventaActividadService';
import { costoActividadService } from '../services/costoActividadService';
import { miembroService } from '../services/miembroService';
import { ActividadEconomica, ProductoActividad, VentaActividad, CostoActividad, Miembro } from '../lib/supabase';
import { ActividadModal } from '../components/ActividadModal';
import { ProductoActividadModal } from '../components/ProductoActividadModal';
import { VentaActividadModal } from '../components/VentaActividadModal';
import { CostoActividadModal } from '../components/CostoActividadModal';
import { ProduccionModal } from '../components/ProduccionModal';
import { ComparativaLotesModal } from '../components/ComparativaLotesModal';
import { RegistrarPagoPedidoModal } from '../components/RegistrarPagoPedidoModal';
import { RegistrarEntregaPedidoModal } from '../components/RegistrarEntregaPedidoModal';
import { DetallePedidoModal } from '../components/DetallePedidoModal';
import { PedidoCard } from '../components/pedidos/PedidoCard';
import { PedidoTable, GrupoPedido } from '../components/pedidos/PedidoTable';
import { PedidosFilters } from '../components/pedidos/PedidosFilters';
import { PedidosEmptyState } from '../components/pedidos/PedidosEmptyState';
import { ConfirmDialog } from '../components/ui/ConfirmDialog';
import { DangerConfirmDialog } from '../components/ui/DangerConfirmDialog';
import { calcularCostoUnitario } from '../utils/costoUnitarioCalculator';
import { getNombreCliente } from '../utils/clienteHelpers';
import { formatCurrency, formatDate } from '../utils/formatters';
import { getCoachingUtilidad, calcularPorcentajeUtilidadPotencial, CoachingUtilidad } from '../utils/coachingUtilidad';
import { usePedidosFilter } from '../hooks/usePedidosFilter';
import { useLazyPedidos } from '../hooks/useLazyPedidos';
import { useToast } from '../components/ui/Toast';
import { CelebrationAlert } from '../components/ui/CelebrationAlert';
import type { CelebrationLogro } from '../components/ui/CelebrationAlert';

// ---------------------------------------------------------------------------
// Helpers de módulo
// ---------------------------------------------------------------------------

function calcularColoresVentas(
  pctVendido: number | null,
  fallback: CoachingUtilidad
): { bgClass: string; textClass: string; barFillClass: string } {
  if (pctVendido === null) return { bgClass: fallback.bgClass, textClass: fallback.textClass, barFillClass: fallback.barFillClass };
  if (pctVendido >= 90) return { bgClass: 'bg-emerald-50', textClass: 'text-emerald-700', barFillClass: 'bg-emerald-500' };
  if (pctVendido >= 60) return { bgClass: 'bg-yellow-50', textClass: 'text-yellow-700', barFillClass: 'bg-yellow-400' };
  if (pctVendido >= 30) return { bgClass: 'bg-orange-50', textClass: 'text-orange-700', barFillClass: 'bg-orange-400' };
  return { bgClass: 'bg-red-50', textClass: 'text-red-700', barFillClass: 'bg-red-400' };
}

function CoachingRow({ c }: { readonly c: CoachingUtilidad }): React.ReactElement | null {
  if (!c.etiqueta) return null;
  return (
    <div className="flex items-center justify-center gap-2 flex-wrap mt-2">
      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${c.badgeClass}`}>
        {c.etiqueta}
      </span>
      <span className={`text-sm ${c.textClass}`}>{c.frase}</span>
      {c.tooltip && (
        <div className="relative group">
          <Info className="w-4 h-4 text-slate-400 cursor-help flex-shrink-0" />
          <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2.5 bg-slate-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-10 pointer-events-none w-[280px] text-left leading-relaxed">
            {c.tooltip}
            <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1 w-2 h-2 bg-slate-900 rotate-45"></div>
          </div>
        </div>
      )}
    </div>
  );
}

async function cargarMiembrosDesde(pedidosData: VentaActividad[]): Promise<Miembro[]> {
  const miembroIds = pedidosData.filter(p => p.miembro_id).map(p => p.miembro_id as string);
  const referidoIds = pedidosData.filter(p => p.miembro_referido_id).map(p => p.miembro_referido_id as string);
  const todosIds = [...new Set([...miembroIds, ...referidoIds])];
  if (todosIds.length === 0) return [];
  return miembroService.getByIds(todosIds);
}

interface ActividadActionsMenuProps {
  readonly showDropdown: boolean;
  readonly onToggleDropdown: () => void;
  readonly onEdit: () => void;
  readonly onArchive: () => void;
  readonly onDelete: () => void;
}

function ActividadActionsMenu({ showDropdown, onToggleDropdown, onEdit, onArchive, onDelete }: ActividadActionsMenuProps) {
  return (
    <div className="flex items-center gap-3">
      <button onClick={onEdit} className="p-2 hover:bg-slate-100 rounded-lg transition-colors">
        <Edit2 className="w-4 h-4" />
      </button>
      <div className="relative">
        <button
          onClick={onToggleDropdown}
          className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
          title="Más acciones"
        >
          <MoreHorizontal className="w-4 h-4" />
        </button>
        {showDropdown && (
          <div className="absolute right-0 mt-2 bg-white rounded-lg shadow-lg border border-slate-200 py-1 z-10 w-auto">
            <button
              onClick={onArchive}
              className="w-full px-3 py-2 text-left text-sm hover:bg-slate-50 flex items-center gap-2 text-amber-600"
            >
              <Archive className="w-4 h-4" />
              <span>Archivar</span>
            </button>
            <button
              onClick={onDelete}
              className="w-full px-3 py-2 text-left text-sm hover:bg-slate-50 flex items-center gap-2 text-red-600"
            >
              <Trash2 className="w-4 h-4" />
              <span>Eliminar</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

interface DetalleActividadPageProps {
  readonly actividadId: string;
  readonly onBack: () => void;
  readonly initialTab?: string;
  readonly readOnly?: boolean;
  readonly lumisialIdOverride?: string;
  readonly ownerContext?: OwnerContext;
}

type TabType = 'resumen' | 'productos' | 'pedidos' | 'costos' | 'producciones';

// Contenido del tooltip para evitar duplicación
const COSTO_UNITARIO_TOOLTIP = "Segun los gastos especificos del producto, los gastos generales y el stock inicial. Formula: (Gastos especificos + Gastos generales) / Stock inicial";

// Función para formatear etiquetas de costos de manera más bonita
const formatearEtiquetaCosto = (etiqueta: string): string => {
  const etiquetas: Record<string, string> = {
    'materia_prima': 'Materia Prima',
    'mano_de_obra': 'Mano de Obra',
    'servicios': 'Servicios',
    'alquiler': 'Alquiler',
    'transporte': 'Transporte',
    'marketing': 'Marketing',
    'administracion': 'Administración',
    'mantenimiento': 'Mantenimiento',
    'energia': 'Energía',
    'telecomunicaciones': 'Telecomunicaciones',
    'seguros': 'Seguros',
    'impuestos': 'Impuestos',
    'insumos': 'Insumos',
    'equipamiento': 'Equipamiento',
    'software': 'Software',
    'licencias': 'Licencias',
    'capacitacion': 'Capacitación',
    'eventos': 'Eventos',
    'publicidad': 'Publicidad',
    'embalaje': 'Embalaje',
    'logistica': 'Logística',
    'almacenamiento': 'Almacenamiento',
    'limpieza': 'Limpieza',
    'seguridad': 'Seguridad',
    'consultoria': 'Consultoría',
    'honorarios': 'Honorarios',
    'comisiones': 'Comisiones',
    'royalties': 'Royalties',
    'patentes': 'Patentes',
    'derechos': 'Derechos',
    'certificaciones': 'Certificaciones',
    'permisos': 'Permisos',
    'licencias_municipales': 'Licencias Municipales',
    'tasas': 'Tasas',
    'aranceles': 'Aranceles',
    'gravamenes': 'Gravámenes',
    'multas': 'Multas',
    'sanciones': 'Sanciones',
    'reparaciones': 'Reparaciones',
    'repuestos': 'Repuestos',
    'garantias': 'Garantías',
    'mantenimiento_preventivo': 'Mantenimiento Preventivo',
    'mantenimiento_correctivo': 'Mantenimiento Correctivo',
    'actualizaciones': 'Actualizaciones',
    'upgrade': 'Upgrade',
    'migracion': 'Migración',
    'implementacion': 'Implementación',
    'instalacion': 'Instalación',
    'configuracion': 'Configuración',
    'personalizacion': 'Personalización',
    'adaptacion': 'Adaptación',
    'integracion': 'Integración',
    'interconexion': 'Interconexión',
    'compatibilidad': 'Compatibilidad',
    'estandarizacion': 'Estandarización',
    'optimizacion': 'Optimización',
    'mejora': 'Mejora',
    'modernizacion': 'Modernización',
    'digitalizacion': 'Digitalización',
    'automatizacion': 'Automatización',
    'robotizacion': 'Robotización',
    'inteligencia_artificial': 'Inteligencia Artificial',
    'machine_learning': 'Machine Learning',
    'analisis_datos': 'Análisis de Datos',
    'big_data': 'Big Data',
    'cloud_computing': 'Cloud Computing',
    'ciberseguridad': 'Ciberseguridad',
    'proteccion_datos': 'Protección de Datos',
    'privacidad': 'Privacidad',
    'confidencialidad': 'Confidencialidad',
    'backup': 'Backup',
    'recuperacion_datos': 'Recuperación de Datos',
    'disaster_recovery': 'Disaster Recovery',
    'continuidad_negocio': 'Continuidad de Negocio',
    'plan_contingencia': 'Plan de Contingencia',
    'gestion_riesgos': 'Gestión de Riesgos',
    'control_calidad': 'Control de Calidad',
    'aseguramiento_calidad': 'Aseguramiento de Calidad',
    'mejora_continua': 'Mejora Continua',
    'procesos': 'Procesos',
    'procedimientos': 'Procedimientos',
    'politicas': 'Políticas',
    'normas': 'Normas',
    'regulaciones': 'Regulaciones',
    'cumplimiento': 'Cumplimiento',
    'auditoria': 'Auditoría',
    'fiscalizacion': 'Fiscalización',
    'inspeccion': 'Inspección',
    'verificacion': 'Verificación',
    'validacion': 'Validación',
    'certificacion': 'Certificación',
    'acreditacion': 'Acreditación',
    'homologacion': 'Homologación',
    'aprobacion': 'Aprobación',
    'autorizacion': 'Autorización',
    'licenciamiento': 'Licenciamiento',
    'habilitacion': 'Habilitación',
    'registro': 'Registro',
    'matricula': 'Matrícula',
    'inscripcion': 'Inscripción',
    'afiliacion': 'Afiliación',
    'suscripcion': 'Suscripción',
    'membresia': 'Membresía',
    'cuota': 'Cuota',
    'suscripcion_mensual': 'Suscripción Mensual',
    'suscripcion_anual': 'Suscripción Anual',
    'pago_unico': 'Pago Único',
    'pago_mensual': 'Pago Mensual',
    'pago_trimestral': 'Pago Trimestral',
    'pago_semestral': 'Pago Semestral',
    'pago_anual': 'Pago Anual',
    'cuota_inicial': 'Cuota Inicial',
    'cuota_mantenimiento': 'Cuota de Mantenimiento',
    'cuota_administracion': 'Cuota de Administración',
    'cuota_servicio': 'Cuota de Servicio',
    'cuota_espacio': 'Cuota de Espacio',
    'cuota_utilizacion': 'Cuota de Utilización',
    'cuota_acceso': 'Cuota de Acceso',
    'cuota_membresia': 'Cuota de Membresía',
    'cuota_subscripcion': 'Cuota de Suscripción',
    'cuota_licencia': 'Cuota de Licencia',
    'cuota_permiso': 'Cuota de Permiso',
    'cuota_autorizacion': 'Cuota de Autorización',
    'cuota_habilitacion': 'Cuota de Habilitación',
    'cuota_registro': 'Cuota de Registro',
    'cuota_inscripcion': 'Cuota de Inscripción',
    'cuota_afiliacion': 'Cuota de Afiliación',
    'cuota_matricula': 'Cuota de Matrícula',
    'cuota_certificacion': 'Cuota de Certificación',
    'cuota_acreditacion': 'Cuota de Acreditación',
    'cuota_homologacion': 'Cuota de Homologación',
    'cuota_aprobacion': 'Cuota de Aprobación'
  };

  // Si la etiqueta está en el diccionario, usar la versión formateada
  if (etiquetas[etiqueta]) {
    return etiquetas[etiqueta];
  }

  // Si no, formatear automáticamente: reemplazar guiones bajos por espacios y capitalizar
  return etiqueta
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char: string) => char.toUpperCase());
};

export const DetalleActividadPage: React.FC<DetalleActividadPageProps> = ({
  actividadId,
  onBack,
  initialTab,
  readOnly = false,
  lumisialIdOverride,
  ownerContext,
}) => {
  const { selectedLumisial } = useLumisial();
  const [searchParams] = useSearchParams();

  const effectiveOwner = useMemo<OwnerContext | null>(() => {
    if (ownerContext) return ownerContext;
    const ownerTipo = searchParams.get('owner');
    const ownerId = searchParams.get('ownerId');
    if (ownerTipo && ownerId && (ownerTipo === 'diocesis' || ownerTipo === 'provincia')) {
      return { tipo: ownerTipo, id: ownerId, nombre: '' };
    }
    if (lumisialIdOverride) {
      return { tipo: 'lumisial', id: lumisialIdOverride, nombre: '' };
    }
    if (selectedLumisial) {
      return { tipo: 'lumisial', id: selectedLumisial.id, nombre: selectedLumisial.nombre };
    }
    return null;
  }, [ownerContext, searchParams, lumisialIdOverride, selectedLumisial]);

  const toast = useToast();
  const [actividad, setActividad] = useState<ActividadEconomica | null>(null);
  const [productos, setProductos] = useState<ProductoActividad[]>([]);
  const [pedidos, setPedidos] = useState<VentaActividad[]>([]);
  const [costos, setCostos] = useState<CostoActividad[]>([]);
  const [miembros, setMiembros] = useState<Miembro[]>([]);
  const [producciones, setProducciones] = useState<ProduccionConMetricas[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<TabType>('resumen');

  // Efecto para establecer la pestaña inicial cuando cambia initialTab
  useEffect(() => {
    if (initialTab && ['resumen', 'productos', 'pedidos', 'costos', 'producciones'].includes(initialTab)) {
      setActiveTab(initialTab as TabType);
    }
  }, [initialTab]);

    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showArchiveDialog, setShowArchiveDialog] = useState(false);
  const [showDeleteCostoDialog, setShowDeleteCostoDialog] = useState(false);
  const [showDeleteProductoDialog, setShowDeleteProductoDialog] = useState(false);
  const [showDeletePedidoDialog, setShowDeletePedidoDialog] = useState(false);
  const [showDeleteProduccionDialog, setShowDeleteProduccionDialog] = useState(false);
  const [costoToDelete, setCostoToDelete] = useState<CostoActividad | null>(null);
  const [productoToDelete, setProductoToDelete] = useState<ProductoActividad | null>(null);
  const [pedidoToDelete, setPedidoToDelete] = useState<VentaActividad | null>(null);
  const [produccionToDelete, setProduccionToDelete] = useState<ProduccionConMetricas | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isProductoModalOpen, setIsProductoModalOpen] = useState(false);
  const [isPedidoModalOpen, setIsPedidoModalOpen] = useState(false);
  const [isCostoModalOpen, setIsCostoModalOpen] = useState(false);
  const [selectedProducto, setSelectedProducto] = useState<ProductoActividad | null>(null);
  const [selectedPedido, setSelectedPedido] = useState<VentaActividad | null>(null);
  const [selectedCosto, setSelectedCosto] = useState<CostoActividad | null>(null);
  const [isPagoModalOpen, setIsPagoModalOpen] = useState(false);
  const [isEntregaModalOpen, setIsEntregaModalOpen] = useState(false);
  const [pedidoParaPago, setPedidoParaPago] = useState<VentaActividad | null>(null);
  const [pedidoParaEntrega, setPedidoParaEntrega] = useState<VentaActividad | null>(null);
  const [isDetallePedidoModalOpen, setIsDetallePedidoModalOpen] = useState(false);
  // Se guarda el id, no el objeto: el detalle se deriva de `pedidos` para que
  // refleje de inmediato cualquier refresh (editar, pago, entrega) sin reabrirlo.
  const [pedidoParaDetalleId, setPedidoParaDetalleId] = useState<string | null>(null);
  const [showActionsDropdown, setShowActionsDropdown] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [vistaTabla, setVistaTabla] = useState(false);
  const [agruparPorProducto, setAgruparPorProducto] = useState(false);
  const [isProduccionModalOpen, setIsProduccionModalOpen] = useState(false);
  const [selectedProduccion, setSelectedProduccion] = useState<ProduccionConMetricas | null>(null);
  const [showComparativaModal, setShowComparativaModal] = useState(false);
  const [celebration, setCelebration] = useState<{ title: string; logros: CelebrationLogro[] } | null>(null);

  const pedidoParaDetalle = useMemo(
    () => pedidos.find(p => p.id === pedidoParaDetalleId) ?? null,
    [pedidos, pedidoParaDetalleId]
  );

  // Hook personalizado para filtrar pedidos
  const {
    filters,
    pedidosFiltrados,
    hasActiveFilters,
    setBusqueda,
    toggleEstadoPago,
    toggleEstadoEntrega,
    toggleFormaEntrega,
    resetFilters
  } = usePedidosFilter({ pedidos, miembros });

  // Hook para lazy loading de pedidos (cargar en grupos de 20)
  const {
    displayedPedidos,
    hasMore,
    remainingCount,
    loadMore,
    reset: resetLazyLoading
  } = useLazyPedidos({ pedidos: pedidosFiltrados, itemsPerPage: 20 });

  const pedidosAgrupados = useMemo<GrupoPedido[]>(() => {
    const map = new Map<string, { producto: typeof productos[number] | undefined; pedidos: typeof displayedPedidos; totalUnidades: number; totalMonto: number }>();
    for (const pedido of displayedPedidos) {
      const key = pedido.producto_id ?? '__sin_producto__';
      if (!map.has(key)) {
        map.set(key, { producto: productos.find(p => p.id === pedido.producto_id), pedidos: [], totalUnidades: 0, totalMonto: 0 });
      }
      const g = map.get(key)!;
      g.pedidos.push(pedido);
      g.totalUnidades += pedido.cantidad;
      g.totalMonto += pedido.monto_total;
    }
    return Array.from(map.values());
  }, [displayedPedidos, productos]);

  const handleCopiarResumen = async () => {
    const abreviarApellido = (nombre: string) => {
      const partes = nombre.trim().split(/\s+/);
      if (partes.length === 1) return nombre;
      const ultimo = partes[partes.length - 1];
      return `${partes.slice(0, -1).join(' ')} ${ultimo[0]}.`;
    };

    const indicadores = (p: typeof pedidosFiltrados[number]) => {
      const badges = [];
      if (p.estado_pago !== 'pagado') badges.push('💰');
      if (p.estado_entrega !== 'entregado') badges.push('📦');
      return badges.length > 0 ? ` ${badges.join(' ')}` : '';
    };

    const filas = pedidosFiltrados.map((p, i) => ({
      num: `${i + 1}.`,
      nombre: abreviarApellido(getNombreCliente(p, miembros)),
      uds: String(p.cantidad),
      monto: formatCurrency(p.monto_total),
      badges: indicadores(p),
    }));

    const anchoNombre = Math.max('Nombre'.length, ...filas.map(f => f.nombre.length));
    const anchoMonto = Math.max('Monto'.length, ...filas.map(f => f.monto.length));
    const anchoUds = Math.max('Un.'.length, ...filas.map(f => f.uds.length));

    const encabezado = `${'#'.padEnd(3)}${'Nombre'.padEnd(anchoNombre + 2)}${'Un.'.padEnd(anchoUds + 2)}${'Monto'.padStart(anchoMonto)}`;
    const separador = '-'.repeat(encabezado.length);

    const lineas = filas.map(f =>
      `${f.num.padEnd(3)}${f.nombre.padEnd(anchoNombre + 2)}${f.uds.padEnd(anchoUds + 2)}${f.monto.padStart(anchoMonto)}${f.badges}`
    );

    const totalMonto = pedidosFiltrados.reduce((s, p) => s + p.monto_total, 0);
    const totalUnidadesVendidas = pedidosFiltrados.reduce((s, p) => s + p.cantidad, 0);
    const stockRestante = productos.reduce((s, p) => s + p.stock_actual, 0);
    const nPedidos = pedidosFiltrados.length;

    const texto = [
      `*Actividad ${actividad?.nombre ?? ''}*`,
      '',
      '📋 *Pedidos*',
      '```',
      encabezado,
      separador,
      ...lineas,
      '```',
      `*${nPedidos} pedido${nPedidos === 1 ? '' : 's'} · ${formatCurrency(totalMonto)}*`,
      `${totalUnidadesVendidas} un. vendidas · ${stockRestante} un. en stock`,
      '',
      '---',
      '💰 Pago pendiente',
      '📦 Entrega pendiente',
      'Sin indicadores = pagado y entregado',
    ].join('\n');

    try {
      await navigator.clipboard.writeText(texto);
      toast.success('Resumen copiado al portapapeles');
    } catch { /* clipboard no disponible */ }
  };

  // Resetear lazy loading cuando cambian los filtros
  useEffect(() => {
    resetLazyLoading();
  }, [pedidosFiltrados.length, resetLazyLoading]);

  useEffect(() => {
    if (actividadId) {
      if (!initialTab) {
        setActiveTab('resumen');
      }
      loadData();
    }
  }, [effectiveOwner, actividadId]);

  // Cerrar dropdown cuando se hace click fuera
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (showActionsDropdown && !(e.target as Element).closest('.relative')) {
        setShowActionsDropdown(false);
      }
    };

    if (showActionsDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showActionsDropdown]);

  const loadData = async () => {
    if (!actividadId) return;

    try {
      setLoading(true);
      const [productosData, pedidosData, costosData] = await Promise.all([
        productoActividadService.getByActividad(actividadId),
        ventaActividadService.getByActividad(actividadId),
        costoActividadService.getByActividad(actividadId)
      ]);

      // Los contratos recalculan derivados en la misma transacción.
      
      // Obtener datos actualizados
      const actividadActualizada = await actividadService.getById(actividadId);

      // Cargar producciones con métricas
      const produccionesData = await produccionService.getByActividadConMetricas(actividadId);

      setActividad(actividadActualizada);
      setProductos(productosData);
      setPedidos(pedidosData);
      setCostos(costosData);
      setProducciones(produccionesData);

      setMiembros(await cargarMiembrosDesde(pedidosData));
    } catch (error) {
      console.error('Error loading activity data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Funciones específicas para evitar recarga completa
  const refreshProductos = async () => {
    try {
      const productosData = await productoActividadService.getByActividad(actividadId);
      setProductos(productosData);
      
      // Leer los derivados confirmados por el contrato.
      const actividadData = await actividadService.getById(actividadId);
      setActividad(actividadData);
    } catch (error) {
      console.error('Error refreshing productos:', error);
    }
  };

  const refreshPedidos = async () => {
    try {
      const pedidosData = await ventaActividadService.getByActividad(actividadId);
      setPedidos(pedidosData);

      // Leer los derivados confirmados por el contrato.
      const actividadData = await actividadService.getById(actividadId);
      setActividad(actividadData);

      // Actualizar miembros relacionados
      setMiembros(await cargarMiembrosDesde(pedidosData));
    } catch (error) {
      console.error('Error refreshing pedidos:', error);
    }
  };

  const refreshCostos = async () => {
    try {
      const costosData = await costoActividadService.getByActividad(actividadId);
      setCostos(costosData);
      
      // Leer los derivados confirmados por el contrato.
      const actividadData = await actividadService.getById(actividadId);
      setActividad(actividadData);
    } catch (error) {
      console.error('Error refreshing costos:', error);
    }
  };

  // Función reutilizable para detectar logros de celebración
  const detectarLogros = async (incluirStock = false) => {
    try {
      const consultas: Promise<any>[] = [ventaActividadService.getByActividad(actividadId)];
      if (incluirStock) {
        consultas.push(
          productoActividadService.getByActividad(actividadId),
          produccionService.getByActividadConMetricas(actividadId)
        );
      }
      const resultados = await Promise.all(consultas);
      const pedidosActualizados = resultados[0] as VentaActividad[];
      setPedidos(pedidosActualizados);

      if (incluirStock) {
        const productosActualizados = resultados[1] as ProductoActividad[];
        const produccionesActualizadas = resultados[2] as ProduccionConMetricas[];
        setProductos(productosActualizados);
        setProducciones(produccionesActualizadas);
      }

      const logros: CelebrationLogro[] = [];
      let title = '';

      if (incluirStock) {
        const productosAct = resultados[1] as ProductoActividad[];
        const produccionesAct = resultados[2] as ProduccionConMetricas[];
        const hayLotes = produccionesAct.length > 0;

        if (hayLotes) {
          // Con lotes: celebración solo por lote completado
          const loteRecienAgotado = produccionesAct.find(l => {
            if (l.stock_disponible !== 0 || l.stock_inicial <= 0 || l.estado !== 'activa') return false;
            const lotePrevio = producciones.find(p => p.id === l.id);
            return lotePrevio ? lotePrevio.stock_disponible > 0 : false;
          });

          if (loteRecienAgotado) {
            title = 'Lote completado';
            const pedidosDelLote = pedidosActualizados.filter(p => !p.anulado && p.produccion_id === loteRecienAgotado.id);
            const unidadesLote = pedidosDelLote.reduce((s, p) => s + Number(p.cantidad), 0);
            logros.push({ tipo: 'stock_agotado', texto: `${loteRecienAgotado.nombre || 'Lote'}: ${unidadesLote} uds. vendidas` });
            if (pedidosDelLote.length > 0 && pedidosDelLote.every(p => p.estado_pago === 'pagado')) {
              logros.push({ tipo: 'todo_pagado', texto: `${unidadesLote} uds. cobradas` });
            }
            if (pedidosDelLote.length > 0 && pedidosDelLote.every(p => p.estado_entrega === 'entregado')) {
              logros.push({ tipo: 'todo_entregado', texto: `${unidadesLote} uds. entregadas` });
            }
          }
        } else {
          // Sin lotes: celebración de actividad completada (stock del producto)
          const todoAgotado = productosAct.length > 0 && productosAct.every(p => p.stock_actual === 0 && p.stock_inicial > 0);
          const antesNoAgotado = productos.some(p => p.stock_actual > 0);

          if (todoAgotado && antesNoAgotado) {
            const pedidosActivos = pedidosActualizados.filter(p => !p.anulado);
            const unidadesVendidas = pedidosActivos.reduce((s, p) => s + Number(p.cantidad), 0);
            title = 'Actividad completada';
            logros.push({ tipo: 'stock_agotado', texto: `Todo el stock vendido (${unidadesVendidas} uds.)` });
            if (pedidosActivos.every(p => p.estado_pago === 'pagado')) {
              logros.push({ tipo: 'todo_pagado', texto: `${unidadesVendidas} uds. cobradas` });
            }
            if (pedidosActivos.every(p => p.estado_entrega === 'entregado')) {
              logros.push({ tipo: 'todo_entregado', texto: `${unidadesVendidas} uds. entregadas` });
            }
          }
        }
      }

      if (logros.length > 0) {
        setCelebration({ title, logros });
      }
    } catch (err) {
      console.error('Error verificando logros:', err);
    }
  };

  const handleDeleteActividad = async () => {
    if (!actividad) return;
    setShowDeleteDialog(true);
  };

  const handleArchiveActividad = async () => {
    if (!actividad) return;
    setShowArchiveDialog(true);
  };

  const confirmDeleteActividad = async () => {
    if (!actividad) return;
    
    try {
      await actividadService.delete(actividad.id);
      onBack();
    } catch (error) {
      console.error('Error deleting actividad:', error);
      toast.error('Error al eliminar la actividad. Por favor, inténtalo de nuevo.');
    }
  };

  const confirmArchiveActividad = async () => {
    if (!actividad) return;
    
    try {
      await actividadService.archivar(actividad.id);
      toast.success('Actividad archivada');
      loadData();
    } catch (error) {
      console.error('Error archiving actividad:', error);
      toast.error('Error al archivar la actividad. Por favor, inténtalo de nuevo.');
    }
  };

  const handleDeleteCosto = (costo: CostoActividad) => {
    setCostoToDelete(costo);
    setShowDeleteCostoDialog(true);
  };

  const confirmDeleteCosto = async () => {
    if (!costoToDelete) return;

    try {
      await costoActividadService.delete(costoToDelete.id);
      
      // Actualizar la lista de costos y los totales de la actividad
      await refreshCostos();
      
      // Cerrar diálogo y limpiar estado
      setShowDeleteCostoDialog(false);
      setCostoToDelete(null);
      
      // Mostrar mensaje de éxito
      toast.success('Gasto eliminado exitosamente');
    } catch (error) {
      console.error('Error deleting costo:', error);
      toast.error('Error al eliminar el gasto. Por favor, intentalo de nuevo.');
    }
  };

  const confirmDeleteProducto = async () => {
    if (!productoToDelete) return;

    try {
      await productoActividadService.delete(productoToDelete.id);
      
      // Actualizar la lista de productos
      await refreshProductos();
      
      // Cerrar diálogo y limpiar estado
      setShowDeleteProductoDialog(false);
      setProductoToDelete(null);
      
      // Mostrar mensaje de éxito
      toast.success('Producto eliminado exitosamente');
    } catch (error) {
      console.error('Error deleting producto:', error);
      toast.error('Error al eliminar el producto. Por favor, inténtalo de nuevo.');
    }
  };

  const confirmDeletePedido = async () => {
    if (!pedidoToDelete) return;
    const pedidoId = pedidoToDelete.id;

    // Cierre optimista: la confirmación y el detalle se cierran de inmediato,
    // sin esperar al delete+refresh (evita el lag de ~1s en que el detalle
    // quedaba abierto tras confirmar).
    setShowDeletePedidoDialog(false);
    setPedidoToDelete(null);
    setIsDetallePedidoModalOpen(false);
    setPedidoParaDetalleId(null);

    try {
      await ventaActividadService.delete(pedidoId);
      await refreshPedidos();
      toast.success('Pedido eliminado exitosamente');
    } catch (error) {
      console.error('Error deleting pedido:', error);
      toast.error('Error al eliminar el pedido. Por favor, inténtalo de nuevo.');
      await refreshPedidos();
    }
  };

  const confirmDeleteProduccion = async () => {
    if (!produccionToDelete) return;

    try {
      await produccionService.delete(produccionToDelete.id);
      
      // Recargar datos
      await loadData();
      
      // Cerrar diálogo y limpiar estado
      setShowDeleteProduccionDialog(false);
      setProduccionToDelete(null);
      
      // Mostrar mensaje de éxito
      toast.success('Lote eliminado exitosamente');
    } catch (error) {
      console.error('Error deleting produccion:', error);
      toast.error('Error al eliminar el lote. Por favor, inténtalo de nuevo.');
    }
  };

  const tabs: Array<{ id: TabType; label: string; shortLabel: string; icon: React.ElementType }> = [
    { id: 'resumen', label: 'Resumen', shortLabel: 'Res.', icon: Briefcase },
    { id: 'productos', label: 'Productos', shortLabel: 'Prod.', icon: Package },
    { id: 'pedidos', label: 'Pedidos', shortLabel: 'Ped.', icon: ShoppingCart },
    { id: 'costos', label: 'Gastos', shortLabel: 'Gas.', icon: Receipt },
    { id: 'producciones', label: 'Lotes', shortLabel: 'Lotes', icon: Layers }
  ];

  const handleTabClick = async (tabId: TabType) => {
    setActiveTab(tabId);
    // Recargar datos relevantes de la pestaña destino en segundo plano
    try {
      if (tabId === 'productos') {
        const [productosData, produccionesData] = await Promise.all([
          productoActividadService.getByActividad(actividadId),
          produccionService.getByActividadConMetricas(actividadId)
        ]);
        setProductos(productosData);
        setProducciones(produccionesData);
      } else if (tabId === 'producciones') {
        const produccionesData = await produccionService.getByActividadConMetricas(actividadId);
        setProducciones(produccionesData);
      } else if (tabId === 'pedidos') {
        await refreshPedidos();
      } else if (tabId === 'resumen') {
        const [productosData, produccionesData] = await Promise.all([
          productoActividadService.getByActividad(actividadId),
          produccionService.getByActividadConMetricas(actividadId)
        ]);
        setProductos(productosData);
        setProducciones(produccionesData);
        const actividadData = await actividadService.getById(actividadId);
        setActividad(actividadData);
      } else if (tabId === 'costos') {
        await refreshCostos();
      }
    } catch (error) {
      console.error('Error al actualizar datos de pestaña:', error);
    }
  };

  const handleNuevoAction = () => {
    switch (activeTab) {
      case 'productos':
        setIsProductoModalOpen(true);
        break;
      case 'pedidos':
        setIsPedidoModalOpen(true);
        break;
      case 'costos':
        setIsCostoModalOpen(true);
        break;
      case 'producciones':
        setIsProduccionModalOpen(true);
        break;
    }
  };

  const renderKpiUtilidad = (
    actividad: ActividadEconomica,
    productos: ProductoActividad[],
    producciones: ProduccionConMetricas[],
    totalCostos: number,
    ingresosCobrados: number,
    resultado: number,
    hayUtilidad: boolean,
    ventasTotales: number
  ) => {
    const ingresoMaximo = productos.reduce((sum, p) => {
      const stockLotes = producciones.filter(l => l.producto_id === p.id).reduce((s, l) => s + l.stock_inicial, 0);
      const stockTotal = stockLotes > 0 ? stockLotes : p.stock_inicial;
      return sum + (p.precio_unitario * stockTotal);
    }, 0);
    const utilidadPotencialMonto = Math.max(ingresoMaximo - totalCostos, 0);
    const pctPotencial = calcularPorcentajeUtilidadPotencial(ingresoMaximo, totalCostos);
    const pctReal = actividad.porcentaje_utilidad;
    const esFinalizada = actividad.estado === 'finalizada' || actividad.estado === 'archivada';
    const hayDatos = totalCostos > 0 || ingresosCobrados > 0;
    const hayDeficit = resultado < 0;

    // Coaching independiente por sección
    const coachingReal = getCoachingUtilidad(pctReal, hayDatos, hayDeficit);
    const coachingPotencial = getCoachingUtilidad(pctPotencial, hayDatos, ingresoMaximo < totalCostos);

    // Semáforo de sección 1: basado en % de unidades vendidas
    const stockTotalInicial = productos.reduce((sum, p) => {
      const stockLotes = producciones.filter(l => l.producto_id === p.id).reduce((s, l) => s + l.stock_inicial, 0);
      return sum + (stockLotes > 0 ? stockLotes : p.stock_inicial);
    }, 0);
    const stockTotalActual = productos.reduce((sum, p) => sum + p.stock_actual, 0);
    const unidadesVendidas = stockTotalInicial - stockTotalActual;
    const pctVendido = stockTotalInicial > 0 ? Math.round((unidadesVendidas / stockTotalInicial) * 100) : null;

    const coloresVentas = calcularColoresVentas(pctVendido, coachingReal);

    const hayVentasPendientes = !esFinalizada && hayDatos && !hayUtilidad && ventasTotales > ingresosCobrados;
    const haySeccionAccion = (pctVendido !== null) || hayVentasPendientes;
    const deficitTextClass = hayDeficit ? 'text-red-700' : 'text-slate-800';
    const pctRealTextClass = pctReal > 0 ? coloresVentas.textClass : deficitTextClass;

    return (
      <div className="rounded-xl border border-slate-200">

        {/* SECCIÓN 1 — Utilidad Real */}
        <div className={`p-4 rounded-t-xl text-center ${coloresVentas.bgClass}`}>
          <p className={`text-xs font-medium uppercase tracking-wide mb-2 ${coloresVentas.textClass}`}>
            Utilidad Real
          </p>
          {hayDatos ? (
            <>
              <div className="flex items-baseline justify-center gap-3">
                <span className={`text-3xl font-bold ${pctRealTextClass}`}>
                  {pctReal > 0 ? `${pctReal}%` : '0%'}
                </span>
                {resultado !== 0 && (
                  <span className={`text-sm ${resultado > 0 ? coloresVentas.textClass : 'text-red-700'}`}>
                    {resultado > 0 ? `+${formatCurrency(resultado)} de ganancia` : `${formatCurrency(resultado)} (déficit)`}
                  </span>
                )}
              </div>
              <CoachingRow c={coachingReal} />
            </>
          ) : (
            <p className="text-sm text-slate-500">{coachingReal.frase}</p>
          )}
        </div>

        {/* SECCIÓN 2 — Indicadores de acción */}
        {haySeccionAccion && (
          <div className="px-4 py-3 border-t border-slate-200 bg-white space-y-2">
            {pctVendido !== null && stockTotalActual > 0 && (
              <div className="flex items-center justify-center gap-1.5">
                <Clock className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" />
                <span className="text-xs font-semibold text-amber-700">
                  {unidadesVendidas === 1 ? 'Vendiste 1 unidad' : `Vendiste ${unidadesVendidas} unidades`}, {stockTotalActual === 1 ? 'vende la restante' : `vende las ${stockTotalActual} restantes`}
                </span>
              </div>
            )}
            {hayVentasPendientes && (
              <div className="flex items-center justify-center gap-1.5">
                <Clock className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" />
                <span className="text-xs font-semibold text-amber-700">
                  Cobra {formatCurrency(ventasTotales - ingresosCobrados)} en ventas pendientes
                </span>
              </div>
            )}
          </div>
        )}

        {/* SECCIÓN 3 — Utilidad Potencial */}
        <div className={`p-4 border-t border-slate-200 rounded-b-xl text-center ${coachingPotencial.bgClass}`}>
          <p className={`text-xs font-medium uppercase tracking-wide mb-2 ${coachingPotencial.textClass}`}>
            Utilidad Potencial
          </p>
          <div className="flex items-baseline justify-center gap-3">
            <span className={`text-3xl font-bold ${pctPotencial > 0 ? coachingPotencial.textClass : 'text-slate-800'}`}>
              {pctPotencial > 0 ? `${pctPotencial}%` : '0%'}
            </span>
            {utilidadPotencialMonto > 0 && (
              <span className={`text-sm ${coachingPotencial.textClass}`}>
                +{formatCurrency(utilidadPotencialMonto)} de ganancia
              </span>
            )}
          </div>
          <div className="flex items-center justify-center gap-1 mt-1">
            <Info className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
            <p className="text-xs text-slate-500">Proyección si se vende todo el stock</p>
          </div>
          <CoachingRow c={coachingPotencial} />
        </div>
      </div>
    );
  };

  const renderResumenKpis = () => {
    if (!actividad) return null;
    const ventasTotales = pedidos.filter(p => !p.anulado).reduce((sum, p) => sum + Number(p.monto_total), 0);
    const ingresosCobrados = actividad.total_ingresos;
    const totalCostos = actividad.total_costos;
    const totalDonaciones = costos.filter(c => c.es_donacion).reduce((sum, c) => sum + Number(c.monto), 0);
    const resultado = ingresosCobrados - totalCostos;
    const hayUtilidad = resultado >= 0;
    const sumaTotal = totalCostos + ingresosCobrados;
    const barCostoPct = sumaTotal > 0 ? (totalCostos / sumaTotal) * 100 : 50;
    const barIngresosPct = sumaTotal > 0 ? (ingresosCobrados / sumaTotal) * 100 : 50;

    return (
      <>
        <div className="mb-6">
          <div className="relative h-8 bg-slate-200 rounded-full overflow-hidden mb-3">
            <div
              className="absolute left-0 top-0 h-full bg-gradient-to-r from-red-400 to-red-500 transition-all duration-500"
              style={{ width: `${barCostoPct}%` }}
            ></div>
            <div
              className="absolute top-0 h-full bg-gradient-to-r from-emerald-400 to-emerald-500 transition-all duration-500"
              style={{ left: `${barCostoPct}%`, width: `${barIngresosPct}%` }}
            ></div>
          </div>
          <div className="flex justify-between items-start">
            <div>
              <p className="text-xs font-medium text-red-700 mb-1">Gastos (Inversion)</p>
              <p className="text-xl font-bold text-red-900">{formatCurrency(totalCostos)}</p>
              {totalDonaciones > 0 && (
                <div className="flex items-center gap-1 mt-1">
                  <Gift className="w-3 h-3 text-emerald-600" />
                  <p className="text-xs font-medium text-emerald-700">Donaciones: {formatCurrency(totalDonaciones)}</p>
                </div>
              )}
            </div>
            <div className="text-right">
              <p className="text-xs font-medium text-emerald-700 mb-1">Ingresos (Ventas)</p>
              <p className="text-xl font-bold text-emerald-900">{formatCurrency(ingresosCobrados)}</p>
            </div>
          </div>
        </div>
        {renderKpiUtilidad(actividad, productos, producciones, totalCostos, ingresosCobrados, resultado, hayUtilidad, ventasTotales)}
      </>
    );
  };

  const getNuevoButtonText = () => {
    switch (activeTab) {
      case 'productos':
        return 'Nuevo Producto';
      case 'pedidos':
        return 'Nuevo Pedido';
      case 'costos':
        return 'Nuevo Gasto';
      case 'producciones':
        return 'Nuevo Lote';
      default:
        return 'Nuevo';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!actividad && !loading) {
    return (
      <div className="min-h-screen bg-slate-50 p-6">
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-xl shadow-md border border-slate-200 p-12 text-center">
            <AlertTriangle className="w-20 h-20 text-amber-500 mx-auto mb-6" />
            <h2 className="text-2xl font-bold text-slate-800 mb-3">Actividad no encontrada</h2>
            <p className="text-slate-600 mb-2">
              La actividad que intentas ver no existe, fue eliminada, o no tienes permiso para acceder a ella.
            </p>
            <p className="text-sm text-slate-500 mb-8">
              Esto puede ocurrir si la actividad pertenece a otro Lumisial o si el registro fue eliminado de la base de datos.
            </p>
            <div className="flex justify-center gap-3">
              <button
                onClick={onBack}
                className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold"
              >
                <ArrowLeft className="w-5 h-5" />
                Volver
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const renderPedidoCard = (pedido: VentaActividad, showProducto = true) => (
    <PedidoCard
      key={pedido.id}
      pedido={pedido}
      productos={productos}
      producciones={producciones}
      miembros={miembros}
      readOnly={readOnly}
      showProducto={showProducto}
      onClickCard={() => { setPedidoParaDetalleId(pedido.id); setIsDetallePedidoModalOpen(true); }}
      onRegistrarPago={() => { setPedidoParaPago(pedido); setIsPagoModalOpen(true); }}
      onRegistrarEntrega={() => { setPedidoParaEntrega(pedido); setIsEntregaModalOpen(true); }}
    />
  );

  const pedidosCardsView = agruparPorProducto ? (
    <div className="space-y-6">
      {pedidosAgrupados.map((grupo) => (
        <div key={grupo.producto?.id ?? '__sin_producto__'}>
          <div className="flex items-center justify-between gap-4 px-1 mb-3">
            <h4 className="font-semibold text-purple-800">
              {grupo.producto?.nombre_producto ?? 'Sin producto'}
            </h4>
            <div className="flex items-center gap-3 text-xs text-purple-600">
              <span>{grupo.totalUnidades} unid. vendidas</span>
              <span className="font-semibold">{formatCurrency(grupo.totalMonto)}</span>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {grupo.pedidos.map(p => renderPedidoCard(p, false))}
          </div>
        </div>
      ))}
    </div>
  ) : (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
      {displayedPedidos.map(p => renderPedidoCard(p))}
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        {/* Versión móvil: 2 filas */}
          <div className="sm:hidden space-y-4">
            {/* Fila 1: Título y volver */}
            <div className="flex items-center gap-4">
              <VolverButton onClick={onBack} />
              <div className="flex-1">
                <h1 className="text-xl font-bold text-slate-900">{actividad!.nombre}</h1>
                <p className="text-sm text-slate-600">{actividad!.descripcion}</p>
              </div>
            </div>
            
            {/* Fila 2: Botones */}
            {!readOnly && (
              <div className="flex items-center justify-end">
                <ActividadActionsMenu
                  showDropdown={showActionsDropdown}
                  onToggleDropdown={() => setShowActionsDropdown(!showActionsDropdown)}
                  onEdit={() => setIsEditModalOpen(true)}
                  onArchive={() => { handleArchiveActividad(); setShowActionsDropdown(false); }}
                  onDelete={() => { handleDeleteActividad(); setShowActionsDropdown(false); }}
                />
              </div>
            )}
          </div>

          {/* Versión desktop: 1 fila */}
          <div className="hidden sm:flex items-center justify-between">
            <div className="flex items-center gap-4">
              <VolverButton onClick={onBack} />
              <div className="flex-1">
                <h1 className="text-2xl font-bold text-slate-900">{actividad!.nombre}</h1>
                <p className="text-slate-600">{actividad!.descripcion}</p>
              </div>
            </div>
            {!readOnly && (
              <ActividadActionsMenu
                showDropdown={showActionsDropdown}
                onToggleDropdown={() => setShowActionsDropdown(!showActionsDropdown)}
                onEdit={() => setIsEditModalOpen(true)}
                onArchive={() => { handleArchiveActividad(); setShowActionsDropdown(false); }}
                onDelete={() => { handleDeleteActividad(); setShowActionsDropdown(false); }}
              />
            )}
          </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200">
        <div className="border-b border-slate-200">
          <nav className="flex -mb-px overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => handleTabClick(tab.id)}
                className={`flex items-center justify-center gap-1 sm:gap-2 px-2 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm font-medium border-b-2 transition-colors flex-1 min-w-0 ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
                }`}
              >
                <tab.icon className="w-5 h-5 sm:w-4 sm:h-4" />
                <span className="truncate">{tab.label}</span>
              </button>
            ))}
          </nav>
        </div>

        <div className="p-6">
          {activeTab === 'resumen' && (
            <div className="max-w-4xl mx-auto">
              {/* Diseño minimalista y elegante */}
              <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-2xl p-8 shadow-sm border border-slate-200">
                
                {renderResumenKpis()}


                {/* Separador */}
                <div className="h-px bg-slate-300 my-6"></div>

                {/* Stock Disponible - Jerarquía Media */}
                <div className="mb-6">
                  <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                    <Package className="w-4 h-4" />
                    Stock Disponible
                  </h4>
                  {productos.length === 0 ? (
                    <p className="text-sm text-slate-500 italic">No hay productos registrados</p>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {productos.map((producto) => {
                        // Verificar si el producto tiene lotes
                        const lotesDelProducto = producciones.filter(p => p.producto_id === producto.id);
                        const tieneLotes = lotesDelProducto.length > 0;
                        
                        let disponible: number;
                        let stockInicial: number;
                        
                        if (tieneLotes) {
                          // Si tiene lotes, usar el stock de los lotes
                          disponible = lotesDelProducto.reduce((sum, l) => sum + l.stock_disponible, 0);
                          stockInicial = lotesDelProducto.reduce((sum, l) => sum + l.stock_inicial, 0);
                        } else {
                          // Si NO tiene lotes, usar stock_actual (refleja ventas descontadas)
                          disponible = producto.stock_actual;
                          stockInicial = producto.stock_inicial;
                        }
                        
                        const porcentajeDisponible = stockInicial > 0 
                          ? (disponible / stockInicial) * 100 
                          : 0;
                        
                        return (
                          <div key={producto.id} className="flex items-center justify-between p-3 bg-white rounded-lg border border-slate-200">
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-slate-900 truncate">{producto.nombre_producto}</p>
                              <p className="text-xs text-slate-500">
                                {disponible} de {stockInicial} unidades
                              </p>
                            </div>
                            <div className="flex-shrink-0 ml-3">
                              <div className={`px-2.5 py-1 rounded-full text-xs font-semibold ${
                                porcentajeDisponible > 50 
                                  ? 'bg-emerald-100 text-emerald-700'
                                  : porcentajeDisponible > 20
                                  ? 'bg-amber-100 text-amber-700'
                                  : 'bg-red-100 text-red-700'
                              }`}>
                                {disponible}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Separador */}
                <div className="h-px bg-slate-300 my-6"></div>

                {/* Duración - Jerarquía Baja */}
                {actividad!.fecha_inicio && actividad!.fecha_fin && (
                  <div>
                    <p className="text-sm text-slate-600">
                      Duración: <span className="font-semibold text-slate-800">{formatDate(actividad!.fecha_inicio)}</span>
                      {' - '}
                      <span className="font-semibold text-slate-800">{formatDate(actividad!.fecha_fin)}</span>
                      {' '}
                      <span className="text-slate-500">
                        ({Math.ceil((new Date(actividad!.fecha_fin).getTime() - new Date(actividad!.fecha_inicio).getTime()) / (1000 * 60 * 60 * 24))} días)
                      </span>
                    </p>
                  </div>
                )}

              </div>
            </div>
          )}

          {activeTab === 'productos' && (
            <div className="space-y-4">
              {productos.length === 0 ? (
                <div className="text-center py-12">
                  <Package className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">No hay productos registrados</p>
                  <button
                    onClick={() => setIsProductoModalOpen(true)}
                    className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    Agregar Primer Producto
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                  {productos.map((producto) => {
                  // Calcular lotes de este producto
                  const lotesDelProducto = producciones.filter(p => p.producto_id === producto.id);
                  const tieneLotes = lotesDelProducto.length > 0;
                  const stockEnLotes = lotesDelProducto.reduce((sum, l) => sum + l.stock_disponible, 0);
                  const stockInicialEnLotes = lotesDelProducto.reduce((sum, l) => sum + l.stock_inicial, 0);
                  
                  return (
                    <div key={producto.id} className="bg-white border border-slate-200 rounded-xl p-4 hover:shadow-md transition-shadow duration-200">
                      {/* Header - Nombre y Precio */}
                      <div className="flex items-center justify-between gap-3 mb-4">
                        <div className="flex-1 min-w-0">
                          <h3 className="text-lg font-bold text-slate-900 truncate">{producto.nombre_producto}</h3>
                          {tieneLotes && (
                            <span className="inline-flex items-center gap-1 text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full mt-1">
                              <Layers className="w-3 h-3" />
                              {lotesDelProducto.length} {lotesDelProducto.length === 1 ? 'lote' : 'lotes'}
                            </span>
                          )}
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className="text-xl sm:text-2xl font-bold text-blue-600 whitespace-nowrap">{formatCurrency(producto.precio_unitario)}</p>
                          <p className="text-xs text-slate-500 mt-0.5">Precio unitario</p>
                        </div>
                      </div>
                      
                      {/* Información adicional y botones en la misma fila */}
                      <div className="flex items-start justify-between gap-3 pt-3 border-t border-slate-100">
                        {/* Indicadores */}
                        <div className="space-y-2 flex-1">
                          {tieneLotes ? (
                            <>
                              {/* Si tiene lotes, mostrar stock de lotes */}
                              <div className="flex items-center gap-1.5">
                                <Layers className="w-3.5 h-3.5 text-purple-500 flex-shrink-0" />
                                <span className="text-sm text-slate-600"><span className="font-semibold text-purple-700">Stock en lotes:</span> <span className="font-bold text-slate-900">{stockEnLotes} / {stockInicialEnLotes}</span></span>
                              </div>
                              <div className="flex items-center gap-1.5">
                                <PackageSearch className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                                <span className="text-sm text-slate-400"><span className="font-semibold">Stock producto:</span> <span className="font-bold">{producto.stock_actual}</span></span>
                                <div className="relative group">
                                  <Info className="w-3.5 h-3.5 text-slate-400 cursor-help" />
                                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2.5 py-2 bg-slate-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-10 pointer-events-none w-[200px] text-left leading-relaxed">
                                    Este producto tiene lotes. El stock se controla a través de los lotes.
                                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1 w-2 h-2 bg-slate-900 rotate-45"></div>
                                  </div>
                                </div>
                              </div>
                            </>
                          ) : (
                            <>
                              {/* Si NO tiene lotes, mostrar stock normal */}
                              <div className="flex items-center gap-1.5">
                                <PackageSearch className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />
                                <span className="text-sm text-slate-600"><span className="font-semibold text-emerald-700">Stock actual:</span> <span className="font-bold text-slate-900">{producto.stock_actual}</span></span>
                              </div>
                              <div className="flex items-center gap-1.5">
                                <Boxes className="w-3.5 h-3.5 text-blue-500 flex-shrink-0" />
                                <span className="text-sm text-slate-600"><span className="font-semibold text-blue-700">Stock inicial:</span> <span className="font-bold text-slate-900">{producto.stock_inicial}</span></span>
                              </div>
                            </>
                          )}
                          <div className="flex items-center gap-1.5">
                            <Receipt className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" />
                            <span className="text-sm text-slate-600"><span className="font-semibold text-amber-700">Gasto unitario:</span> <span className="font-bold text-slate-900">{formatCurrency(calcularCostoUnitario(producto.id, productos, costos, producciones).costoUnitario)}</span></span>
                            <div className="relative group">
                              <Info className="w-3.5 h-3.5 text-slate-400 cursor-help" />
                              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2.5 py-2 bg-slate-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-10 pointer-events-none w-[200px] sm:w-[260px] text-left leading-relaxed">
                                {COSTO_UNITARIO_TOOLTIP}
                                <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1 w-2 h-2 bg-slate-900 rotate-45"></div>
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        {/* Botones de acción */}
                        {!readOnly && (
                          <div className="flex flex-col gap-2 flex-shrink-0">
                            <button
                              onClick={() => {
                                setSelectedProducto(producto);
                                setIsProductoModalOpen(true);
                              }}
                              className="p-2 text-slate-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all duration-200"
                              title="Editar producto"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => {
                                setProductoToDelete(producto);
                                setShowDeleteProductoDialog(true);
                              }}
                              className="p-2 text-red-600 hover:text-red-900 hover:bg-red-50 rounded-lg transition-all duration-200"
                              title="Eliminar producto"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                  })}
                </div>
              )}
            </div>
          )}

          {activeTab === 'pedidos' && (
            <div className="space-y-4">
              {/* Componente de Filtros */}
              <PedidosFilters
                filters={filters}
                showFilters={showFilters}
                hasActiveFilters={hasActiveFilters}
                vistaTabla={vistaTabla}
                agruparPorProducto={agruparPorProducto}
                onBusquedaChange={setBusqueda}
                onToggleFilters={() => setShowFilters(!showFilters)}
                onToggleEstadoPago={toggleEstadoPago}
                onToggleEstadoEntrega={toggleEstadoEntrega}
                onToggleFormaEntrega={toggleFormaEntrega}
                onResetFilters={resetFilters}
                onToggleVista={() => setVistaTabla(v => !v)}
                onToggleAgrupacion={() => setAgruparPorProducto(v => !v)}
                onCopiarResumen={handleCopiarResumen}
              />

              {pedidosFiltrados.length === 0 ? (
                <PedidosEmptyState
                  hasActiveFilters={hasActiveFilters}
                  onAddPedido={() => setIsPedidoModalOpen(true)}
                />
              ) : (
                <>
                  {vistaTabla ? (
                    <PedidoTable
                      pedidos={displayedPedidos}
                      grupos={agruparPorProducto ? pedidosAgrupados : undefined}
                      productos={productos}
                      producciones={producciones}
                      miembros={miembros}
                      readOnly={readOnly}
                      onClickRow={(pedido) => {
                        setPedidoParaDetalleId(pedido.id);
                        setIsDetallePedidoModalOpen(true);
                      }}
                      onRegistrarPago={(pedido) => {
                        setPedidoParaPago(pedido);
                        setIsPagoModalOpen(true);
                      }}
                      onRegistrarEntrega={(pedido) => {
                        setPedidoParaEntrega(pedido);
                        setIsEntregaModalOpen(true);
                      }}
                    />
                  ) : pedidosCardsView}

                  {/* Botón "Cargar más" */}
                  {hasMore && (
                    <div className="flex flex-col items-center gap-3 mt-6 pt-6 border-t border-slate-200">
                      <p className="text-sm text-slate-600">
                        Mostrando {displayedPedidos.length} de {pedidosFiltrados.length} pedidos
                      </p>
                      <button
                        onClick={loadMore}
                        className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium flex items-center gap-2"
                      >
                        <Package className="w-4 h-4" />
                        Cargar {Math.min(remainingCount, 20)} pedidos más
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {activeTab === 'costos' && (
            <div className="space-y-4">
              {costos.length === 0 ? (
                <div className="text-center py-12">
                  <Receipt className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">No hay gastos registrados</p>
                  <button
                    onClick={() => setIsCostoModalOpen(true)}
                    className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    Agregar Primer Gasto
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                  {[...costos].sort((a, b) => (b.requiere_reembolso ? 1 : 0) - (a.requiere_reembolso ? 1 : 0)).map((costo) => (
                    <div key={costo.id} className={`bg-white rounded-xl p-4 hover:shadow-md transition-shadow duration-200 ${
                      costo.es_donacion
                        ? 'border-2 border-emerald-200 bg-emerald-50/30'
                        : costo.requiere_reembolso
                          ? 'border-2 border-amber-300'
                          : 'border border-slate-200'
                    }`}>
                      {/* Header - Descripción y Monto en la misma fila */}
                      <div className="flex items-center justify-between gap-3 mb-4">
                        <div className="flex-1 min-w-0">
                          <h3 className="text-lg font-bold text-slate-900 leading-tight">{costo.descripcion}</h3>
                          {costo.es_donacion && (
                            <span className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700">
                              <Gift className="w-3 h-3" />
                              Donación
                            </span>
                          )}
                          {costo.requiere_reembolso && (
                            <span className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-700">
                              <RefreshCw className="w-3 h-3" />
                              Pendiente de reembolso
                            </span>
                          )}
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className={`text-xl sm:text-2xl font-bold whitespace-nowrap ${costo.es_donacion ? 'text-emerald-600' : 'text-red-600'}`}>{formatCurrency(costo.monto)}</p>
                        </div>
                      </div>
                      
                      {/* Información adicional y botones en la misma fila */}
                      <div className="flex items-start justify-between gap-3 pt-3 border-t border-slate-100">
                        {/* Indicadores */}
                        <div className="space-y-2 flex-1">
                          <div className="flex items-center gap-1.5">
                            <Calendar className="w-3.5 h-3.5 text-blue-500 flex-shrink-0" />
                            <span className="text-sm text-slate-600"><span className="font-semibold text-slate-900">{formatDate(costo.fecha)}</span></span>
                          </div>
                          <div className="flex items-center gap-1.5">
                            <Tag className="w-3.5 h-3.5 text-purple-500 flex-shrink-0" />
                            <span className="text-sm text-slate-600"><span className="font-semibold text-slate-900">{formatearEtiquetaCosto(costo.tipo_costo)}</span></span>
                          </div>
                          <div className="flex items-center gap-1.5">
                            {costo.producto_id ? (
                              <>
                                <Package className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" />
                                <span className="text-sm text-slate-600">
                                  <span className="font-semibold text-amber-700">
                                    {productos.find(p => p.id === costo.producto_id)?.nombre_producto || 'Producto'}
                                  </span>
                                </span>
                              </>
                            ) : (
                              <>
                                <FolderOpen className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                                <span className="text-sm text-slate-600">
                                  <span className="font-semibold text-slate-700">Gasto general</span>
                                </span>
                              </>
                            )}
                          </div>
                          {costo.produccion_id && (
                            <div className="flex items-center gap-1.5">
                              <Layers className="w-3.5 h-3.5 text-purple-500 flex-shrink-0" />
                              <span className="text-sm text-slate-600">
                                <span className="font-semibold text-purple-700">
                                  {producciones.find(p => p.id === costo.produccion_id)?.nombre || 'Lote'}
                                </span>
                              </span>
                            </div>
                          )}
                        </div>
                        
                        {/* Botones de acción */}
                        {!readOnly && (
                          <div className="flex flex-col gap-2 flex-shrink-0">
                            <button
                              onClick={() => {
                                setSelectedCosto(costo);
                                setIsCostoModalOpen(true);
                              }}
                              className="p-2 text-slate-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all duration-200"
                              title="Editar gasto"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteCosto(costo)}
                              className="p-2 text-red-600 hover:text-red-900 hover:bg-red-50 rounded-lg transition-all duration-200"
                              title="Eliminar gasto"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'producciones' && (
            <div className="space-y-4">
              {producciones.length === 0 ? (
                <div className="text-center py-12">
                  <Layers className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-slate-700 mb-2">No hay lotes creados</h3>
                  
                  {/* Recomendaciones de cuándo crear lotes */}
                  <div className="bg-purple-50 border border-purple-200 rounded-xl p-6 max-w-lg mx-auto text-left mb-6">
                    <h4 className="font-semibold text-purple-800 mb-3 flex items-center gap-2">
                      <CheckCircle className="w-5 h-5" />
                      ¿Cuándo crear lotes?
                    </h4>
                    <ul className="space-y-2 text-sm text-purple-700">
                      <li className="flex items-start gap-2">
                        <span className="text-green-500 mt-0.5">✓</span>
                        Se fabrica el mismo producto en diferentes tandas (ej: Empanadas Enero, Empanadas Febrero)
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-green-500 mt-0.5">✓</span>
                        Se quiere analizar la rentabilidad de cada lote por separado
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-green-500 mt-0.5">✓</span>
                        Se necesita control de stock y gastos por cada lote
                      </li>
                    </ul>
                    
                    <h4 className="font-semibold text-slate-600 mt-4 mb-2 flex items-center gap-2">
                      <XCircle className="w-5 h-5 text-slate-400" />
                      No es necesario cuando:
                    </h4>
                    <ul className="space-y-2 text-sm text-slate-600">
                      <li className="flex items-start gap-2">
                        <span className="text-slate-400 mt-0.5">✗</span>
                        Es una actividad única (ej: Venta de Garage o Feria del Libro 2026)
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-slate-400 mt-0.5">✗</span>
                        No importa separar los lotes
                      </li>
                    </ul>
                  </div>
                  
                  {productos.length === 0 ? (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 max-w-md mx-auto">
                      <p className="text-amber-800 text-sm">
                        <Package className="w-4 h-4 inline mr-1" />
                        Primero debes crear al menos un producto en la pestaña "Productos"
                      </p>
                    </div>
                  ) : (
                    <button
                      onClick={() => setIsProduccionModalOpen(true)}
                      className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                    >
                      Crear Primer Lote
                    </button>
                  )}
                </div>
              ) : (
                <>
                  {/* Header */}
                  <div className="mb-4">
                    <p className="text-slate-600">
                      {producciones.length} {producciones.length === 1 ? 'lote' : 'lotes'}
                    </p>
                  </div>
                  
                  {/* Lista de lotes */}
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                    {producciones.map((produccion) => (
                      <div 
                        key={produccion.id} 
                        className="bg-white border border-slate-200 rounded-xl p-4 hover:shadow-md transition-shadow duration-200"
                      >
                        {/* Header con producto */}
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <div className="bg-purple-100 p-2 rounded-lg">
                              <Layers className="w-5 h-5 text-purple-600" />
                            </div>
                            <div>
                              <h3 className="font-bold text-slate-900">{produccion.nombre}</h3>
                              {produccion.producto && (
                                <p className="text-xs text-purple-600 flex items-center gap-1">
                                  <Package className="w-3 h-3" />
                                  {produccion.producto.nombre_producto}
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-1">
                            <span className={`text-xs px-2 py-0.5 rounded-full ${
                              produccion.estado === 'activa'
                                ? 'bg-green-100 text-green-700'
                                : 'bg-slate-100 text-slate-600'
                            }`}>
                              {produccion.estado === 'activa' ? 'Activa' : 'Finalizada'}
                            </span>
                            {!readOnly && (
                              <>
                                <button
                                  onClick={() => {
                                    setSelectedProduccion(produccion);
                                    setIsProduccionModalOpen(true);
                                  }}
                                  className="p-1.5 text-slate-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all"
                                  title="Editar lote"
                                >
                                  <Edit2 className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => {
                                    setProduccionToDelete(produccion);
                                    setShowDeleteProduccionDialog(true);
                                  }}
                                  className="p-1.5 text-slate-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
                                  title="Eliminar lote"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </>
                            )}
                          </div>
                        </div>

                        {/* Stock info */}
                        <div className="bg-slate-50 rounded-lg p-2 mb-3 flex items-center justify-between">
                          <span className="text-xs text-slate-600">Stock:</span>
                          <span className="text-sm font-semibold">
                            {produccion.stock_disponible} / {produccion.stock_inicial} disponibles
                          </span>
                        </div>
                        
                        {/* Fechas si existen */}
                        {(produccion.fecha_inicio || produccion.fecha_fin) && (
                          <div className="flex items-center gap-1.5 text-sm text-slate-600 mb-3">
                            <Calendar className="w-3.5 h-3.5 text-slate-400" />
                            {produccion.fecha_inicio && formatDate(produccion.fecha_inicio)}
                            {produccion.fecha_inicio && produccion.fecha_fin && ' - '}
                            {produccion.fecha_fin && formatDate(produccion.fecha_fin)}
                          </div>
                        )}
                        
                        {/* Métricas */}
                        <div className="grid grid-cols-2 gap-3 pt-3 border-t border-slate-100">
                          <div>
                            <p className="text-xs text-slate-500">Ingresos</p>
                            <p className="font-semibold text-green-600">{formatCurrency(produccion.total_ingresos)}</p>
                          </div>
                          <div>
                            <p className="text-xs text-slate-500">Gastos</p>
                            <p className="font-semibold text-red-600">{formatCurrency(produccion.total_costos)}</p>
                          </div>
                          <div>
                            <p className="text-xs text-slate-500">Utilidad Real</p>
                            <p className={`font-semibold ${produccion.utilidad_real >= 0 ? 'text-blue-600' : 'text-red-600'}`}>
                              {formatCurrency(produccion.utilidad_real)}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-slate-500">Pedidos</p>
                            <p className="font-semibold text-slate-700">{produccion.cantidad_pedidos}</p>
                          </div>
                        </div>
                        
                        {/* Utilidad objetivo calculada automáticamente */}
                        {produccion.utilidad_objetivo_calculada > 0 && (() => {
                          const metaCumplida = produccion.utilidad_real >= produccion.utilidad_objetivo_calculada;
                          const todoVendido = produccion.stock_disponible === 0 && produccion.stock_inicial > 0;
                          const pct = Math.min(100, Math.max(0, (produccion.utilidad_real / produccion.utilidad_objetivo_calculada) * 100));

                          return (
                            <div className="mt-3 pt-3 border-t border-slate-100">
                              <div className="flex justify-between text-sm mb-1">
                                {todoVendido && metaCumplida ? (
                                  <span className="text-green-600 font-semibold flex items-center gap-1">
                                    <CheckCircle className="w-3.5 h-3.5" />
                                    ¡Meta cumplida!
                                  </span>
                                ) : todoVendido ? (
                                  <span className="text-slate-500">Meta (todo vendido):</span>
                                ) : (
                                  <span className="text-slate-500">Meta (si se vende todo):</span>
                                )}
                                <span className={`font-medium ${metaCumplida ? 'text-green-600' : ''}`}>
                                  {formatCurrency(produccion.utilidad_objetivo_calculada)}
                                </span>
                              </div>
                              <div className="w-full bg-slate-200 rounded-full h-2">
                                <div
                                  className={`h-2 rounded-full transition-all duration-500 ${
                                    metaCumplida ? 'bg-green-500' : 'bg-purple-500'
                                  }`}
                                  style={{ width: `${pct}%` }}
                                />
                              </div>
                              {!todoVendido && pct > 0 && (
                                <p className="text-xs text-slate-400 mt-1 text-right">{Math.round(pct)}% de la meta</p>
                              )}
                            </div>
                          );
                        })()}
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      {isEditModalOpen && (
        <ActividadModal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          onSuccess={() => {
            loadData();
            toast.success('Actividad actualizada');
          }}
          actividad={actividad!}
        />
      )}

      {isProductoModalOpen && (
        <ProductoActividadModal
          isOpen={isProductoModalOpen}
          onClose={() => {
            setIsProductoModalOpen(false);
            setSelectedProducto(null);
          }}
          onSuccess={() => {
            refreshProductos();
            toast.success(selectedProducto ? 'Producto actualizado' : 'Producto agregado');
          }}
          actividadId={actividadId}
          producto={selectedProducto || undefined}
          actividadNombre={actividad?.nombre || ''}
          esPrimerProducto={productos.length === 0 && !selectedProducto}
          cantidadLotes={selectedProducto ? producciones.filter(p => p.producto_id === selectedProducto.id).length : 0}
        />
      )}

      {isPedidoModalOpen && (
        <VentaActividadModal
          isOpen={isPedidoModalOpen}
          onClose={() => {
            setIsPedidoModalOpen(false);
            setSelectedPedido(null);
            if (pedidoParaDetalle) {
              setIsDetallePedidoModalOpen(true);
            }
          }}
          onSuccess={async () => {
            refreshPedidos();
            toast.success(selectedPedido ? 'Pedido actualizado' : 'Pedido registrado');
            if (pedidoParaDetalle) {
              setIsDetallePedidoModalOpen(true);
            }
            // Detectar logros (incluir stock porque es una venta nueva)
            await detectarLogros(true);
          }}
          actividadId={actividadId}
          venta={selectedPedido || undefined}
          miembros={miembros}
          sinAsociacionMiembro={effectiveOwner?.tipo !== 'lumisial'}
          level={pedidoParaDetalle ? 'nested' : 'base'}
        />
      )}

      {isCostoModalOpen && (
        <CostoActividadModal
          isOpen={isCostoModalOpen}
          onClose={() => {
            setIsCostoModalOpen(false);
            setSelectedCosto(null);
          }}
          onSuccess={() => {
            refreshCostos();
            toast.success(selectedCosto ? 'Gasto actualizado' : 'Gasto agregado');
          }}
          actividadId={actividadId}
          costo={selectedCosto || undefined}
        />
      )}

      {isPagoModalOpen && pedidoParaPago && (
        <RegistrarPagoPedidoModal
          isOpen={isPagoModalOpen}
          onClose={() => {
            setIsPagoModalOpen(false);
            setPedidoParaPago(null);
            if (pedidoParaDetalle) {
              setIsDetallePedidoModalOpen(true);
            }
          }}
          onSuccess={async () => {
            refreshPedidos();
            toast.success('Pago registrado');
            if (pedidoParaDetalle) {
              setIsDetallePedidoModalOpen(true);
            }
            await detectarLogros(false);
          }}
          pedido={pedidoParaPago}
          level={pedidoParaDetalle ? 'nested' : 'base'}
        />
      )}

      {isEntregaModalOpen && pedidoParaEntrega && (
        <RegistrarEntregaPedidoModal
          isOpen={isEntregaModalOpen}
          onClose={() => {
            setIsEntregaModalOpen(false);
            setPedidoParaEntrega(null);
            if (pedidoParaDetalle) {
              setIsDetallePedidoModalOpen(true);
            }
          }}
          onSuccess={async () => {
            refreshPedidos();
            toast.success('Entrega registrada');
            if (pedidoParaDetalle) {
              setIsDetallePedidoModalOpen(true);
            }
            await detectarLogros(false);
          }}
          pedido={pedidoParaEntrega}
          level={pedidoParaDetalle ? 'nested' : 'base'}
        />
      )}

      {/* Modal de Detalle del Pedido */}
      {isDetallePedidoModalOpen && pedidoParaDetalle && (
        <DetallePedidoModal
          isOpen={isDetallePedidoModalOpen}
          onClose={() => {
            setIsDetallePedidoModalOpen(false);
            setPedidoParaDetalleId(null);
          }}
          pedido={pedidoParaDetalle}
          miembros={miembros}
          productos={productos}
          onEdit={readOnly ? undefined : () => {
            setSelectedPedido(pedidoParaDetalle);
            setIsPedidoModalOpen(true);
          }}
          onDelete={readOnly ? undefined : () => {
            // No cerrar el detalle: la confirmación se apila encima (level="nested").
            // Cancelar vuelve al detalle; confirmar elimina y cierra ambos.
            setPedidoToDelete(pedidoParaDetalle);
            setShowDeletePedidoDialog(true);
          }}
          onRegistrarPago={readOnly ? undefined : () => {
            setPedidoParaPago(pedidoParaDetalle);
            setIsPagoModalOpen(true);
          }}
          onRegistrarEntrega={readOnly ? undefined : () => {
            setPedidoParaEntrega(pedidoParaDetalle);
            setIsEntregaModalOpen(true);
          }}
        />
      )}

      {/* Modal de Lote */}
      {isProduccionModalOpen && (
        <ProduccionModal
          isOpen={isProduccionModalOpen}
          onClose={() => {
            setIsProduccionModalOpen(false);
            setSelectedProduccion(null);
          }}
          onSuccess={async () => {
            loadData();
            toast.success(selectedProduccion ? 'Lote actualizado' : 'Lote creado');
          }}
          actividadId={actividadId}
          produccion={selectedProduccion || undefined}
          productos={productos}
          produccionesExistentes={producciones}
        />
      )}

      {/* Botón Flotante (FAB) - Solo visible en pestañas que permiten agregar y no es readOnly */}
      {activeTab !== 'resumen' && !readOnly && (
        <button
          onClick={handleNuevoAction}
          className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 flex items-center gap-2 sm:gap-3 px-3 py-2 sm:px-5 sm:py-3 bg-blue-600 text-white rounded-lg shadow-lg hover:bg-blue-700 hover:shadow-xl transition-all duration-200 z-40"
        >
          <Plus className="w-4 h-4 sm:w-5 sm:h-5" />
          <span className="font-medium text-xs sm:text-sm">{getNuevoButtonText()}</span>
        </button>
      )}

      {/* Diálogos de Confirmación */}
      <DangerConfirmDialog
        isOpen={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        onConfirm={confirmDeleteActividad}
        title="⚠️ Eliminar Actividad Permanentemente"
        message={`Estás a punto de eliminar permanentemente la actividad "${actividad?.nombre}".`}
        warningItems={[
          'Se eliminarán TODOS los productos registrados en esta actividad.',
          'Se eliminarán TODOS los pedidos y sus pagos asociados.',
          'Se eliminaran TODOS los gastos registrados.',
          'Se ANULARÁN TODOS los movimientos financieros (ingresos y egresos) en el balance.',
          'Los movimientos anulados quedarán marcados como anulados en el historial.',
          'Esta acción es IRREVERSIBLE.'
        ]}
        checkboxLabel="He leído y entiendo que esta acción eliminará permanentemente todos los datos financieros asociados a esta actividad y no se podrán recuperar."
        confirmText="Eliminar Permanentemente"
        cancelText="Cancelar"
      />

      <ConfirmDialog
        isOpen={showArchiveDialog}
        onClose={() => setShowArchiveDialog(false)}
        onConfirm={confirmArchiveActividad}
        title="Archivar Actividad"
        message={`¿Estás seguro de que quieres archivar la actividad "${actividad?.nombre}"? Podrás restaurarla más tarde.`}
        confirmText="Archivar"
        cancelText="Cancelar"
        type="archive"
      />

      <ConfirmDialog
        isOpen={showDeleteCostoDialog}
        onClose={() => {
          setShowDeleteCostoDialog(false);
          setCostoToDelete(null);
        }}
        onConfirm={confirmDeleteCosto}
        title="Eliminar Gasto"
        message={`Estas seguro de que quieres eliminar el gasto "${costoToDelete?.descripcion}" por ${formatCurrency(costoToDelete?.monto || 0)}? Esta accion anulara el gasto y su movimiento asociado.`}
        confirmText="Eliminar"
        cancelText="Cancelar"
        type="delete"
      />

      <ConfirmDialog
        isOpen={showDeleteProductoDialog}
        onClose={() => {
          setShowDeleteProductoDialog(false);
          setProductoToDelete(null);
        }}
        onConfirm={confirmDeleteProducto}
        title="Eliminar Producto"
        message={`¿Estás seguro de que quieres eliminar el producto "${productoToDelete?.nombre_producto}" por ${formatCurrency(productoToDelete?.precio_unitario || 0)}? Esta acción eliminará el producto y todos sus pedidos asociados.`}
        confirmText="Eliminar"
        cancelText="Cancelar"
        type="delete"
      />

      <ConfirmDialog
        isOpen={showDeletePedidoDialog}
        onClose={() => {
          setShowDeletePedidoDialog(false);
          setPedidoToDelete(null);
        }}
        onConfirm={confirmDeletePedido}
        title="Eliminar Pedido"
        message={`¿Estás seguro de que quieres eliminar el pedido de "${pedidoToDelete ? getNombreCliente(pedidoToDelete, miembros) : 'desconocido'}" por ${formatCurrency(pedidoToDelete?.monto_total || 0)}? Esta acción eliminará el pedido y todos sus pagos y entregas asociados.`}
        confirmText="Eliminar"
        cancelText="Cancelar"
        type="delete"
        level="nested"
      />

      <ConfirmDialog
        isOpen={showDeleteProduccionDialog}
        onClose={() => {
          setShowDeleteProduccionDialog(false);
          setProduccionToDelete(null);
        }}
        onConfirm={confirmDeleteProduccion}
        title="Eliminar Lote"
        message={`¿Estás seguro de que quieres eliminar el lote "${produccionToDelete?.nombre}"? Se eliminaran tambien todos los gastos y pedidos asociados a este lote.`}
        confirmText="Eliminar"
        cancelText="Cancelar"
        type="delete"
      />

      {/* Modal de Comparativa de Lotes */}
      <ComparativaLotesModal
        isOpen={showComparativaModal}
        onClose={() => setShowComparativaModal(false)}
        producciones={producciones}
      />

      {/* Aviso de celebración cuando se agota stock */}
      {celebration && (
        <CelebrationAlert
          title={celebration.title}
          logros={celebration.logros}
          duration={6000}
          onClose={() => setCelebration(null)}
        />
      )}
    </div>
  );
};
